import type { ComputedRef, Ref } from 'vue';

import { computed, ref } from 'vue';

import type { Cession } from '../services/capitalMarketsService';

type CessionsDataGetter = () => Cession[] | undefined;
type SortableColumn = keyof Cession;

type SortDirection = '' | 'asc' | 'desc';

type SortState = {
  column: string;
  direction: SortDirection;
};

type UseTableSortingReturn = {
  handleSort: (_column: string) => void;
  sortedCessions: ComputedRef<Cession[]>;
  sortState: Ref<SortState>;
};

const NUMERIC_COLUMNS: readonly SortableColumn[] = [
  'financed_amount',
  'gross_amount',
  'acquisition_amount',
] as const;

const DATE_COLUMNS: readonly SortableColumn[] = [
  'operation_date',
  'cut_off_date',
] as const;

const SORT_DIRECTIONS = {
  ASC: 'asc' as const,
  DESC: 'desc' as const,
  NONE: '' as const,
} as const;

const DEFAULT_NUMERIC_VALUE = 0;
const DEFAULT_DATE_VALUE = 0;
const DEFAULT_STRING_VALUE = '';

const isNumericColumn = (column: string): boolean => (
  NUMERIC_COLUMNS.includes(column as SortableColumn)
);

const isDateColumn = (column: string): boolean => (
  DATE_COLUMNS.includes(column as SortableColumn)
);

/**
 * Hook to manage table sorting for cessions
 *
 * @param cessions - Function that returns the array of cessions to sort
 * @returns Object with sort state, sorted cessions and sort function
 *
 * @example
 * ```typescript
 * const { sortState, sortedCessions, handleSort } = useTableSorting(() => cessions.value);
 * ```
 */
export default function useTableSorting(cessions: CessionsDataGetter): UseTableSortingReturn {
  const sortState = ref<SortState>({
    column: SORT_DIRECTIONS.NONE,
    direction: SORT_DIRECTIONS.NONE,
  });

  const getColumnValue = (item: Cession, column: string): number | string => {
    const actualColumn = column as SortableColumn;

    if (isNumericColumn(actualColumn)) {
      return (item[actualColumn] as number) ?? DEFAULT_NUMERIC_VALUE;
    }

    if (isDateColumn(actualColumn)) {
      const dateValue = item[actualColumn] as string | undefined;
      return dateValue ? new Date(dateValue).getTime() : DEFAULT_DATE_VALUE;
    }

    const stringValue = item[actualColumn];
    return stringValue?.toString().toLowerCase() ?? DEFAULT_STRING_VALUE;
  };

  const compareValues = (
    firstValue: number | string,
    secondValue: number | string,
    isAscending: boolean,
  ): number => {
    if (typeof firstValue === 'number' && typeof secondValue === 'number') {
      return isAscending ? firstValue - secondValue : secondValue - firstValue;
    }

    const firstStr = String(firstValue);
    const secondStr = String(secondValue);

    return isAscending
      ? firstStr.localeCompare(secondStr)
      : secondStr.localeCompare(firstStr);
  };

  const sortedCessions = computed<Cession[]>(() => {
    const data = cessions();
    if (!data || data.length === 0) {
      return [];
    }

    const { column, direction } = sortState.value;
    if (!column || direction === SORT_DIRECTIONS.NONE) {
      return data;
    }

    const isAscending = direction === SORT_DIRECTIONS.ASC;

    return [...data].sort((firstItem, secondItem) => {
      const firstValue = getColumnValue(firstItem, column);
      const secondValue = getColumnValue(secondItem, column);
      return compareValues(firstValue, secondValue, isAscending);
    });
  });

  const handleSort = (column: string): void => {
    const currentColumn = sortState.value.column;
    const currentDirection = sortState.value.direction;

    if (currentColumn === column) {
      sortState.value.direction = currentDirection === SORT_DIRECTIONS.ASC
        ? SORT_DIRECTIONS.DESC
        : SORT_DIRECTIONS.ASC;
    } else {
      sortState.value.column = column;
      sortState.value.direction = SORT_DIRECTIONS.ASC;
    }
  };

  return {
    handleSort,
    sortedCessions,
    sortState,
  };
}
